import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SetFormActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import {
  checkZonePagesListForNexPageUrls,
  createRedirectionUrlFromNavigationOptions,
  flattenObject,
  isLastPage,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService, IconCacheService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  BaseCssProperties,
  ContainerType,
  NavigationOptionEnum,
  NavigationOptionsType,
  NewUiZoneWidgetConfig,
  ProviderClass,
  UiPageConfig,
  UiZoneWidgetAttributeConfig,
  WidgetNames,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { BaseComponent } from '../../base/base.component';
import { ConfirmationHelperService } from '@app/finxone-web-frontend/app/lib/services/confirmation-helper/confirmation-helper.service';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-onboarding-button',
  templateUrl: './onboarding-button.component.html',
  styleUrl: './onboarding-button.component.scss',
})
export class OnboardingButtonComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public icon: string;
  public iconSize: baseCssConfigStyle;
  public activePageIndex!: number;
  public zonePagesList: UiPageConfig[];
  public pagesMapping: { [key: string]: Record<string, string | boolean> } = {};
  public redirectionPath: string;
  public text = '';
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public renderedContent = '';
  public formID: string;
  public currentZoneFromRoute: any;

  private readonly store = inject(Store);
  public readonly configService = inject(ConfigService);
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly countriesCurrenciesService = inject(CountriesCurrenciesService);
  private readonly iconCacheService = inject(IconCacheService);
  private readonly accountService = inject(AccountService);
  private readonly workflowFormsService = inject(WorkflowFormsService);
  private readonly alertHandlerService = inject(AlertHandlerService);
  private readonly zonePageUrlProvider = inject(ZonePageUrlProvider);
  private readonly confirmationHelperService = inject(ConfirmationHelperService);

  constructor(public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>) {
    super();
    this.currentZoneFromRoute = this.activeRoute?.snapshot?.params;
    this.getCurrentPageIndexByPageUrl();
    this.getZonePagesListData();
  }

  ngOnInit() {
    this.formID = this.dataIn?.attri?.widgetProperties?.['parentForm'];
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri;
      this.icon = this.attri.widgetProperties?.icon ?? '';
      this.onLoadWidgetContent();
      this.getWidgetConfig();
    }
  }

  private onLoadWidgetContent(): void {
    this.text = this.dataIn.attri?.widgetProperties?.textContent ?? '';
    const template = Handlebars.compile(this.text);
    buildHandleBarModel(
      this.store,
      this.text,
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.accountService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
      });
  }

  private getWidgetConfig() {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.BOX_SHADOW,
      BaseCssProperties.TEXTALIGN,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.COLOR,
      BaseCssProperties.V_ALIGN,
      BaseCssProperties.BORDER,
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.TEXTALIGN,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXT_ITALIC,
      BaseCssProperties.TEXT_UNDERLINE,
      BaseCssProperties.TEXT_BOLD,
      BaseCssProperties.TEXT_FONT_STYLE,
    ]);
    this.iconSize = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.ICON_COLOR,
      BaseCssProperties.ICON_SIZE,
    ]);
    this.getGlobalStyling();
  }

  private getGlobalStyling() {
    if (
      this.dataIn.attri.widgetProperties?.['globalStylingClass'] &&
      this.dataIn.attri.widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass = this.dataIn.attri.widgetProperties?.['globalStylingClass'] ?? '';
      this.isAllowedGlobalStyling = this.dataIn.attri.widgetProperties?.['globalStyling'] ?? false;
    }
  }

  public pageAction() {
    if (this.activePageIndex === undefined) {
      return;
    }

    const currentPageValidation = this.workflowFormsService.validateCurrentPageForms();
    if (!currentPageValidation.isValid) {
      this.workflowFormsService.markCurrentPageFormsTouched();
    }

    const { nextPageUrl, pageType, pageId } = this.getNextPageConfig();
    const currentPageForms = this.workflowFormsService.getCurrentPageForms();
    const formValues = flattenObject(this.readCurrentPageFormsValues(currentPageForms));

    const hasEmailKey = formValues && 'isEmailVerified' in formValues;
    const isEmailVerified = formValues?.['isEmailVerified'];

    const hasPhoneKey = formValues && 'isPhoneVerified' in formValues;
    const isPhoneVerified = formValues?.['isPhoneVerified'];

    if ((hasEmailKey && !isEmailVerified) || (hasPhoneKey && !isPhoneVerified)) {
      this.alertHandlerService.showAlertFn('error', 'Email or phone not verified.');
      console.warn('Email or phone not verified.');
      return;
    }

    if (nextPageUrl) {
      this.redirectionPath = this.updateRedirectPath(nextPageUrl);
      if (currentPageValidation.isValid || currentPageValidation?.invalidForms?.length === 0) {
        this.updateFormState();
        if (!isLastPage(this.activePageIndex, this.zonePagesList) && this.redirectionPath) {
          const navigationOptions: NavigationOptionsType = {
            navigationOption:
              pageType === ContainerType.MODAL
                ? NavigationOptionEnum.USE_AS_MODAL
                : NavigationOptionEnum.USE_AS_NAVIGATION,
            zone: APP_ZONES.ONBOARDING,
            pageUrl: nextPageUrl,
            pageId: pageId ?? '',
            role: this.currentZoneFromRoute?.['role'],
            tabRef: '',
            pageTransition: 'forward',
          };
          const url = this.determineRedirectUrl(navigationOptions, true);
          this._router.navigateByUrl(url);
        }
      } else {
        this.workflowFormsService.markCurrentPageFormsTouched();
      }
    }
  }

  private determineRedirectUrl(params: NavigationOptionsType, replaceUrl = false): string {
    let url = createRedirectionUrlFromNavigationOptions(params, replaceUrl);
    if (this.confirmationHelperService.reconfirmationConfig()) {
      url = this.confirmationHelperService.reconfirmationConfig()?.redirectUrl || url;
      this.confirmationHelperService.clearReconfirmationConfig();
    }
    return url;
  }

  private getZonePagesListData() {
    this.configService.getCurrentRoleZonePagesConfig().subscribe((response: UiPageConfig[]) => {
      this.zonePagesList = response;
      this.checkPagesWithForms(this.zonePagesList);
    });
  }

  private updateFormState() {
    // Dynamic form Values
    const currentPageForms = this.workflowFormsService.getCurrentPageForms();
    const formValues = this.readCurrentPageFormsValues(currentPageForms);
    const formId = this.workflowFormsService.generateCustomFormId(this.formID, this.zonePageUrlProvider);
    const form = this.workflowFormsService.getFormPart(formId);
    if (!form) return;

    this.store.dispatch(
      new SetFormActionWithId(
        {
          type: '',
          formData: { ...(formValues[formId] || {}) },
        },
        this.formID,
      ),
    );
  }

  private readCurrentPageFormsValues(currentPageForms: string[]): Record<string, any> {
    const formValues: Record<string, any> = {};
    currentPageForms.forEach((formId) => {
      const formValue = this.workflowFormsService.getFormValues(formId);
      if (formValue) {
        formValues[formId] = formValue;
      }
    });
    return formValues;
  }

  private getNextPageConfig() {
    const nextPageIndex = this.activePageIndex + 1;
    let nextPageUrl: string | undefined;
    let pageType: ContainerType | undefined;
    let pageId: string | undefined;

    if (this.pagesMapping[nextPageIndex]?.['isVerificationPage']) {
      const nextPageInfo = checkZonePagesListForNexPageUrls(
        nextPageIndex,
        this.zonePagesList,
        this.pagesMapping,
      );
      nextPageUrl = nextPageInfo?.url;
      pageType = nextPageInfo?.pageType;
      pageId = nextPageInfo?.pageId;
    } else {
      nextPageUrl = this.zonePagesList[nextPageIndex]?.url ?? '/';
      pageType = this.zonePagesList[nextPageIndex]?.containerType ?? ContainerType.PAGE;
      pageId = this.zonePagesList[nextPageIndex]?.pageId;
    }

    return { nextPageUrl, pageType, pageId };
  }

  public checkPagesWithForms(zonePagesList: UiPageConfig[]) {
    zonePagesList.forEach((page, index) => {
      const isVerificationPage = this.checkIfPageIsEmailOrPhoneVerificationPage(page);
      if (isVerificationPage) {
        this.pagesMapping[index] = {
          ...this.pagesMapping[index],
          isVerificationPage: isVerificationPage,
        };
      }
    });
  }

  private checkIfPageIsEmailOrPhoneVerificationPage(page: UiPageConfig): boolean {
    return !!page?.widgetLayouts?.find((widget) => this.widgetContainsOtpWidget(widget));
  }

  // To check if a widget layout or any of its children contain OTP_VERIFICATION
  private widgetContainsOtpWidget(widgetLayout: NewUiZoneWidgetConfig): boolean {
    // Check if the widget itself is a OTP_VERIFICATION
    if (widgetLayout?.name === WidgetNames.OTP_VERIFICATION) {
      return true;
    }
    // Recursively check all child components
    if (widgetLayout?.childComponents?.length) {
      return widgetLayout?.childComponents.some((childWidget) =>
        this.widgetContainsOtpWidget(childWidget as NewUiZoneWidgetConfig),
      );
    }
    return false;
  }

  async getCurrentPageIndexByPageUrl() {
    const result = await firstValueFrom(
      this.configService.getPageUrlPageIndex(
        this.currentZoneFromRoute?.zone,
        this.currentZoneFromRoute?.page || this.currentZoneFromRoute?.['modal-page'],
      ),
    );

    if (result !== undefined) {
      this.activePageIndex = result;
    }
  }

  private updateRedirectPath(newPath: string): string {
    const path = location.pathname.split('/');
    path[3] = newPath;
    return path.join('/');
  }
}
